/**
 * A script as a table: each stateful component, source and bag down the side,
 * and what it is at each step across, from step zero (where everything
 * starts) to the last. Read from the previews, so the table and the drawing
 * never disagree.
 */
import type { DesignDocument, NodeDocument, TemplateDocument } from "../design/document";
import type { SignalShape } from "../workspace/signals";
import type { ConditionSetDocument, RunPump, ScriptDocument, SourceKind } from "../design/protocol";
import { flowIn, fromCanonical, toCanonical, TUBING_SET_UNITS, volumeIn, type LiquidUnits } from "../workspace/units";
import { operationText, profileShort, secondsText, sourceValueText, stepTime, stepTimeText, trackVolumes } from "./operations";
import { instrumentTag, isInlinePort, liveConditions, type PortPreview, type Readout, type RegulatorPreview, type StepPreview } from "./preview";

export type RowKind = "time" | "state" | "setpoint" | "reading" | "source" | "bag";

/** How a cell reads: passing or running, blocking or stopped, a source's kind, or plain. */
export type Tone = "on" | "off" | SourceKind | "liquid" | "plain";

export interface MatrixCell {
  text: string;
  /** A second line: what a bag holds, under its volume. */
  sub?: string;
  /** Not what the column before had. */
  changed: boolean;
  tone: Tone;
  /** A pump's operation in the step, drawn as a glyph before the text. */
  operation?: RunPump;
  /**
   * Drawn in place of the text (which stays its name and hover): a state or
   * a port's status as a glyph, with `short` beside it where there is a
   * number to read, as a pressure's magnitude.
   */
  glyph?: SignalShape;
  short?: string;
  /** An instrument's reading: as sure as the preview knows it (see Readout). */
  certainty?: Readout["certainty"];
}

/** A component's state, as the table draws it; others read as their name. */
const STATE_GLYPHS: Readonly<Record<string, SignalShape>> = {
  open: "valve_open",
  closed: "valve_closed",
  connected: "connected",
  disconnected: "disconnected",
  on: "run",
  off: "off",
};

export interface MatrixRow {
  kind: RowKind;
  /** The component: for a source, the port component it sits on. */
  id: string;
  label: string;
  /** A port joining two lines: open, and not settable. */
  inline?: boolean;
  /** A pump: its cells are operations, edited in the step, not cycled. */
  pump?: boolean;
  /** A chamber among the bags: its contents follow from the steps, never set. */
  chamber?: boolean;
  cells: MatrixCell[];
}

const ROLE_ORDER = ["valve", "pump", "mixer", "aseptic_connector", "variable_volume_chamber"];

const byLabel = (a: NodeDocument, b: NodeDocument) =>
  (a.label || a.local_id).localeCompare(b.label || b.local_id, undefined, { numeric: true });

/** Whether a component's states are a motor's (run or stop) rather than a way through. */
export function isMotor(node: Pick<NodeDocument, "role">): boolean {
  return node.role === "pump" || node.role === "mixer";
}

/** Whether a state reads as stopped or shut: what gets the red badge. */
export function blocks(node: Pick<NodeDocument, "role">, state: string, passes: boolean): boolean {
  if (isMotor(node)) return /^(off|stop)/.test(state);
  return !passes;
}

/** A gauge in pascal as a person reads it: "−60 kPa", "+50 kPa". */
function kPa(magnitude: number): string {
  const value = Number((Math.abs(magnitude) / 1000).toPrecision(4));
  return `${magnitude < 0 ? "\u2212" : "+"}${value} kPa`;
}

/** What a port's source is set to, in a few characters: "−60 kPa", "atm", "sealed". */
export function portText(port: Pick<PortPreview, "kind" | "magnitude" | "profile"> | undefined, units: LiquidUnits = TUBING_SET_UNITS): string {
  if (!port) return "sealed";
  if (port.profile) return profileShort(port.profile, units);
  switch (port.kind) {
    case "open":
      return "atm";
    case "sealed":
      return "sealed";
    case "flow":
      return port.magnitude != null ? flowIn(port.magnitude, units, 6) : "flow";
    default:
      return port.magnitude != null ? kPa(port.kind === "vacuum" ? -Math.abs(port.magnitude) : port.magnitude) : port.kind;
  }
}

/** What a regulator is asked to hold, in a few characters: "+100 kPa", a sweep's span, or "off" with none. */
export function regulatorText(regulator: RegulatorPreview | undefined, units: LiquidUnits = TUBING_SET_UNITS): string {
  if (!regulator) return "off";
  if (regulator.sweep) return profileShort(regulator.sweep, units);
  if (regulator.asked === null || regulator.asked <= 0) return "off";
  const sign = regulator.gas === "vacuum" ? -1 : 1;
  // Starved: it holds what its supply gives.
  const short = regulator.held !== undefined && regulator.held < regulator.asked;
  return short ? `${kPa(sign * regulator.asked)}, holds ${kPa(sign * regulator.held!)}` : kPa(sign * regulator.asked);
}

/** A gauge, zero as itself rather than signed. */
function gauge(value: number): string {
  return value === 0 ? "0 kPa" : kPa(value);
}

/** What an instrument reads, in a few characters: "+30 kPa", "≤ +100 kPa", "liquid", "10 mL/min", "flowing", "—". */
export function readoutText(readout: Readout | undefined, units: LiquidUnits = TUBING_SET_UNITS): string {
  if (!readout) return "—";
  if (readout.state) return readout.state;
  const quantity = (value: number) => (readout.observes === "flow_rate" ? (value === 0 ? `0 ${units.flowRate.label}` : flowIn(value, units, 3)) : gauge(value));
  if (readout.certainty === "exact" && readout.value !== undefined) return quantity(readout.value);
  if (readout.certainty === "bound" && readout.low !== undefined && readout.high !== undefined) {
    if (readout.low === 0) return `≤ ${quantity(readout.high)}`;
    if (readout.high === 0) return `≥ ${quantity(readout.low)}`;
    return `${quantity(readout.low)} to ${quantity(readout.high)}`;
  }
  return "—";
}

/** The step's endpoint on an instrument, as its alarm limit reads: "≥ +40 kPa", "air". */
export function limitText(readout: Readout | undefined, units: LiquidUnits = TUBING_SET_UNITS): string | null {
  const limit = readout?.limit;
  if (!limit) return null;
  const symbol = { lt: "<", le: "≤", gt: ">", ge: "≥" }[limit.comparison];
  // A detector reads 1 seeing air (D60).
  if (readout.observes === "air_in_line") return meetsAir(limit) ? "air" : "liquid";
  const value = readout.observes === "flow_rate" ? flowIn(limit.value, units, 3) : readout.observes === "pressure" ? gauge(limit.value) : String(limit.value);
  return `${symbol} ${value}`;
}

const meetsAir = (limit: NonNullable<Readout["limit"]>) => (limit.comparison === "ge" || limit.comparison === "gt" ? limit.value <= 1 : limit.value < 0.5);

/** A volume in cubic metres in the design's volume unit (D49): "10,000 mL". */
export function volumeLabel(volume: number, units: LiquidUnits = TUBING_SET_UNITS): string {
  return volumeIn(volume, units, 6);
}

/** A tracked volume, nominal: to the whole display unit, "190 mL" (D60). */
export function nominalVolumeLabel(volume: number, units: LiquidUnits = TUBING_SET_UNITS): string {
  return volumeIn(toCanonical(Math.round(fromCanonical(volume, units.volume)), units.volume), units, 6);
}

/** The next state in a component's set, round to the first. */
export function nextState(stateSet: readonly string[], current: string | undefined): string {
  const at = current === undefined ? -1 : stateSet.indexOf(current);
  return stateSet[(at + 1) % stateSet.length]!;
}

/**
 * The table: `columns[0]` is step zero, `columns[i]` what step `i` leaves.
 * Only components a script sets, sources, and bags: the rest never changes.
 */
export function stateMatrix(
  design: DesignDocument,
  conditions: ConditionSetDocument,
  columns: readonly StepPreview[],
  /** The script the columns are of: for each step's time, and what its pumps do (D45). */
  script: ScriptDocument | null = null,
  /** What volumes and flows read in (D49). */
  units: LiquidUnits = TUBING_SET_UNITS,
  /** The design's templates: each part's hold-up volume. */
  templates: Readonly<Record<string, TemplateDocument>> = {},
): MatrixRow[] {
  const nodes = design.nodes ?? [];
  const rows: MatrixRow[] = [];
  const steps = script?.steps ?? [];
  // Drawn supplies are sources at their rating until the condition set gives them one (D66).
  conditions = liveConditions(design, templates, conditions);
  const volumes = script ? trackVolumes(design, conditions, script, columns, templates) : [];
  // Compared by `key` where a cell has one: a bag's contents, not how its first column reads.
  const withChanges = (cells: (Omit<MatrixCell, "changed"> & { key?: string })[]): MatrixCell[] =>
    cells.map(({ key, ...cell }, index) => ({
      ...cell,
      changed: index > 0 && (key ?? cell.text) !== (cells[index - 1]!.key ?? cells[index - 1]!.text),
    }));

  const stateful = nodes
    // A bag's sealed or released is not what the preview turns on: its row is what it holds.
    .filter((node) => node.state_set && node.state_set.length > 0 && node.role !== "reservoir")
    .sort((a, b) => {
      const ra = ROLE_ORDER.indexOf(a.role);
      const rb = ROLE_ORDER.indexOf(b.role);
      return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb) || byLabel(a, b);
    });
  if (script) {
    rows.push({
      kind: "time",
      id: "time",
      label: "Time",
      cells: columns.map((_, index) => {
        const step = index > 0 ? steps[index - 1] : undefined;
        return { text: step ? stepTimeText(stepTime(step)) : "—", tone: "plain", changed: false };
      }),
    });
  }
  for (const node of stateful) {
    if (node.role === "pump" && script) {
      // What it does in the step: its operation, or where the step before left it.
      rows.push({
        kind: "state",
        id: node.local_id,
        label: node.label || node.local_id,
        pump: true,
        cells: withChanges(
          columns.map((column, index) => {
            const step = index > 0 ? steps[index - 1] : undefined;
            const operation = step?.actions?.find((action): action is RunPump => action.kind === "pump" && action.target === node.local_id);
            const state = column.states[node.local_id] ?? node.initial_state ?? "off";
            const text = operation ? operationText(operation, units) : state === "on" ? (index > 0 ? "running" : "on") : "off";
            return { text, tone: text === "off" ? "off" : "on", ...(operation ? { operation } : { glyph: text === "off" ? "off" : "run" }) };
          }),
        ),
      });
      continue;
    }
    rows.push({
      kind: "state",
      id: node.local_id,
      label: node.label || node.local_id,
      cells: withChanges(
        columns.map((column) => {
          const state = column.states[node.local_id] ?? node.initial_state ?? node.state_set![0]!;
          const passes = column.nodes[node.local_id]?.passes ?? true;
          const glyph = STATE_GLYPHS[state];
          return { text: state, tone: blocks(node, state, passes) ? "off" : "on", ...(glyph ? { glyph } : {}) };
        }),
      ),
    });
  }

  // Each regulator: what it is asked to hold in each step (D65).
  for (const node of nodes.filter((candidate) => candidate.role === "regulator").sort(byLabel)) {
    rows.push({
      kind: "setpoint",
      id: node.local_id,
      label: `${node.label || node.local_id} · ${instrumentTag(node, "PIC")}`,
      cells: withChanges(
        columns.map((column) => {
          const regulator = column.regulators?.[node.local_id];
          const text = regulatorText(regulator, units);
          const tone: Tone = text === "off" ? "open" : regulator?.gas === "vacuum" ? "vacuum" : regulator?.gas === "pressure" ? "pressure" : "plain";
          return { text, tone };
        }),
      ),
    });
  }

  // Each instrument: what it reads after each step, as surely as the preview knows it, and the step's limit on it.
  for (const node of nodes.filter((candidate) => candidate.role === "sensor" && columns.some((column) => column.readouts?.[candidate.local_id])).sort(byLabel)) {
    const tag = columns.map((column) => column.readouts?.[node.local_id]?.tag).find(Boolean);
    rows.push({
      kind: "reading",
      id: node.local_id,
      label: tag && tag !== (node.label || node.local_id) ? `${node.label || node.local_id} · ${tag}` : node.label || node.local_id,
      cells: withChanges(
        columns.map((column) => {
          const readout = column.readouts?.[node.local_id];
          const text = readoutText(readout, units);
          const limit = limitText(readout, units);
          const value = readout?.value ?? readout?.high ?? 0;
          const tone: Tone =
            readout?.limit?.unreachable ? "off" : readout?.state === "liquid" ? "liquid" : readout?.observes === "pressure" && value > 0 ? "pressure" : readout?.observes === "pressure" && (readout.value ?? readout.low ?? 0) < 0 ? "vacuum" : "plain";
          return { text, ...(limit ? { sub: `limit ${limit}` } : {}), tone, ...(readout ? { certainty: readout.certainty } : {}) };
        }),
      ),
    });
  }

  // Every port, and anything else a source sits on: an unsourced port is open at its end (D59), else sealed.
  const sourced = new Set((conditions.sources ?? []).map((source) => source.port.split(".")[0]!));
  const ports = nodes.filter((node) => node.role === "port" || sourced.has(node.local_id)).sort(byLabel);
  for (const node of ports) {
    // A connection between two lines is open throughout: nothing a step sets.
    if (isInlinePort(design, node) && !sourced.has(node.local_id)) {
      rows.push({
        kind: "source",
        id: node.local_id,
        label: node.label || node.local_id,
        inline: true,
        cells: withChanges(columns.map(() => ({ text: "open", tone: "on" as Tone, glyph: "through" as const }))),
      });
      continue;
    }
    rows.push({
      kind: "source",
      id: node.local_id,
      label: node.label || node.local_id,
      cells: withChanges(
        columns.map((column) => {
          const port = column.ports[node.local_id];
          // A staircase: its span, and its increment and hold beneath.
          const sub =
            port?.profile?.mode === "stairs" && port.profile.increment
              ? `by ${sourceValueText(port.profile.increment, port.profile.quantity, units)}${port.profile.hold ? ` · ${secondsText(port.profile.hold)}` : ""}`
              : undefined;
          const text = portText(port, units);
          const kind = port?.kind ?? "sealed";
          const glyph: SignalShape | undefined =
            kind === "sealed" ? "sealed" : kind === "pressure" ? "pressure" : kind === "vacuum" ? "vacuum" : undefined;
          // A pressure or vacuum keeps its number beside its glyph; atmosphere reads "atm".
          const short = kind === "pressure" || kind === "vacuum" ? text : undefined;
          return { text, ...(sub ? { sub } : {}), tone: kind, ...(glyph ? { glyph } : {}), ...(short ? { short } : {}) };
        }),
      ),
    });
  }

  // Bags, and chambers once liquid reaches them: what each holds, and how much where the arithmetic knows.
  const holds = (id: string) => columns.some((column) => column.nodes[id]?.liquid);
  const vessels = nodes
    .filter((node) => node.role === "reservoir" || ((node.role === "chamber" || node.role === "variable_volume_chamber") && holds(node.local_id)))
    .sort((a, b) => Number(a.role !== "reservoir") - Number(b.role !== "reservoir") || byLabel(a, b));
  for (const bag of vessels) {
    rows.push({
      kind: "bag",
      id: bag.local_id,
      label: bag.label || bag.local_id,
      chamber: bag.role !== "reservoir",
      cells: withChanges(
        columns.map((column, index) => {
          const liquid = column.nodes[bag.local_id]?.liquid ?? null;
          const fluid = liquid ? (column.fluidLabels[liquid] ?? liquid) : "air";
          const tracked = script ? volumes[index]?.[bag.local_id] : undefined;
          const volume = tracked ?? (index === 0 ? column.volumes[bag.local_id] : undefined);
          const tone: Tone = liquid ? "liquid" : "plain";
          // Unknown after a pressure-driven move: the fluid, and a question mark for how much.
          if (volume === undefined || volume === null)
            return liquid && tracked === null && index > 0 ? { text: fluid, sub: `? ${units.volume.label}`, key: `${fluid}?`, tone } : { text: fluid, key: fluid, tone };
          if (volume === 0 && !liquid) return { text: "empty", key: "empty", tone };
          // As set at step zero; after a step nominal, to the whole unit (D60).
          return { text: index === 0 ? volumeLabel(volume, units) : nominalVolumeLabel(volume, units), sub: fluid, key: `${fluid}${volume}`, tone };
        }),
      ),
    });
  }
  return rows;
}
